import { Button, Text, Toggle, Tooltip } from '@shared/components';
import { CircularProgress } from '@shared/components/plugin-components';
import { useToast } from '@shared/components/toast';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useLogosContext } from '@shared/contexts/pluginContexts/LogosURLsContext';
import {
  GetActivePostingsDocument,
  GetLoadBoardPostingsDocument,
  IntegrationType,
  IsMovementPostedDocument,
  LoadBoardPosting,
  usePostToLoadBoardMutation,
  useRefreshPostingOnLoadBoardMutation,
  useShipmentDistanceLazyQuery,
  useUnpostFromLoadBoardMutation,
} from '@shared/generated/graphql';
import useGetActivePostings from '@shared/graphql/hooks/movements/useGetActivePostings';
import { useShipmentDetails } from '@shared/hooks/shipments/useShipmentDetails';
import { Shipment } from '@shared/types/shipment';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import { isNil } from 'lodash';
import { useState } from 'react';
import { MdOutlineOpenInNew, MdRefresh } from 'react-icons/md';
import { useEnabledLoadboardIntegrations } from './hooks/useEnabledLoadboardIntegrations';
import { InformationNeeded } from './InformationNeeded';
import { PostingUserAutocomplete } from './PostingUserAutocomplete';

const ROOT = makeRootClassName('LoadboardDetails');
export const el = makeElementClassNameFactory(ROOT);

export const LOADBOARDS_REQUIRING_EXTERNAL_ID_MAPPING = [IntegrationType.DAT];

type LoadboardDetailsProps = {
  shipmentId: string;
};

type MovementLoadboardDetailsProps = {
  movementId: string;
  shipment: Shipment;
};

export const MovementLoadboardDetails = ({
  movementId,
  shipment,
}: MovementLoadboardDetailsProps) => {
  const loadboardIntegrations = useEnabledLoadboardIntegrations();

  const { postings, isLoading } = useGetActivePostings({
    id: movementId,
  });

  return (
    <div className="flex flex-col gap-2">
      <Text type="body-sm" className="text-gray-700">
        Loadboards
      </Text>
      {isLoading ? (
        <div className="flex items-center justify-center">
          <CircularProgress size="xs" />
        </div>
      ) : (
        loadboardIntegrations.map((integration) => (
          <LoadBoardPostingLine
            key={integration}
            integrationType={integration}
            posting={postings.find((p) => p.integrationType === integration)}
            shipmentId={shipment.id}
          />
        ))
      )}
    </div>
  );
};

export const LoadboardDetails = ({ shipmentId }: LoadboardDetailsProps) => {
  const { shipment } = useShipmentDetails({ shipmentId });
  const movementId = shipment?.movementPreviews?.[0]?.id;
  if (!movementId)
    return (
      <div className="flex items-center justify-center">
        <CircularProgress size="xs" />
      </div>
    );

  return (
    <MovementLoadboardDetails movementId={movementId} shipment={shipment} />
  );
};

export function stringifyIntegrationType(integrationType: IntegrationType) {
  switch (integrationType) {
    case IntegrationType.ALJEX:
      return 'Aljex';
    case IntegrationType.DAT:
      return 'DAT';
    case IntegrationType.HOSTED_MCLEOD:
      return 'McLeod';
    case IntegrationType.TURVO:
      return 'Turvo';
    default:
      return integrationType;
  }
}

export function getIntegrationLogo(
  integrationType: IntegrationType,
  logos: ReturnType<typeof useLogosContext>
) {
  switch (integrationType) {
    case IntegrationType.ALJEX:
      return logos.ALJEX;
    case IntegrationType.DAT:
      return logos.DAT;
    case IntegrationType.HOSTED_MCLEOD:
      return logos.HOSTED_MCLEOD;
    case IntegrationType.TURVO:
      return logos.TURVO;
    case IntegrationType.REVENOVA:
      return logos.REVENOVA;
    default:
      return logos.DAT;
  }
}

export function getIntegrationTooltip(integrationType: IntegrationType) {
  switch (integrationType) {
    case IntegrationType.ALJEX:
      return 'Aljex integrated loadboard';
    case IntegrationType.DAT:
      return 'DAT loadboard';
    case IntegrationType.HOSTED_MCLEOD:
      return 'McLeod integrated loadboard';
    case IntegrationType.TURVO:
      return 'Turvo integrated loadboard';
    case IntegrationType.REVENOVA:
      return 'Revenova integrated loadboard';
    default:
      return `${integrationType} loadboard`;
  }
}

const isPostedToLoadBoard = (
  integrationType: IntegrationType,
  posting?: LoadBoardPosting
) => {
  const requiresExternalId =
    LOADBOARDS_REQUIRING_EXTERNAL_ID_MAPPING.includes(integrationType);
  if (requiresExternalId) {
    return !isNil(posting) && !isNil(posting.externalId);
  }
  return !isNil(posting);
};

type LoadBoardPostingLineProps = {
  shipmentId: string;
  posting: LoadBoardPosting | undefined;
  integrationType: IntegrationType;
};

function LoadBoardPostingLine({
  shipmentId,
  posting,
  integrationType,
}: LoadBoardPostingLineProps) {
  const { me, defaultOrgId } = useMeContext();
  const { sendToast } = useToast();
  const [getDistance] = useShipmentDistanceLazyQuery({
    variables: { id: shipmentId },
  });
  const [postToLB, { loading: postLoading }] = usePostToLoadBoardMutation();
  const [unpostFromLB, { loading: unpostLoading }] =
    useUnpostFromLoadBoardMutation();
  const [refreshPosting, { loading: refreshLoading }] =
    useRefreshPostingOnLoadBoardMutation();
  const lbString = stringifyIntegrationType(integrationType);
  const logos = useLogosContext();
  const loadboardsRequiringPhoneNumber = [IntegrationType.DAT];
  const hasPhoneNumber = !isNil(me?.profile?.phoneNumber);
  const [postingUserId, setPostingUserId] = useState<string | null>(null);

  // NOTE(martin): values based on news sites, 11 Aug 2025
  // 5 = 2x the average rate
  const PER_MILE_LOW_THRESHOLD = 0.75;
  const PER_MILE_HIGH_THRESHOLD = 5;

  const checkDistanceAndPost = async () => {
    try {
      const result = await getDistance();
      const usdPerMile = result.data?.getShipmentDistance?.usdPerMile;

      if (
        usdPerMile &&
        (usdPerMile < PER_MILE_LOW_THRESHOLD ||
          usdPerMile > PER_MILE_HIGH_THRESHOLD)
      ) {
        sendToast('Confirm Posting', {
          description: `Please confirm you want to post this load to ${lbString}. USD per mile: $${usdPerMile.toFixed(
            2
          )}`,
          variant: 'info',
          actions: [
            {
              label: 'Post',
              onPress: async () => {
                await postToLoadBoard();
              },
            },
          ],
        });
      } else {
        await postToLoadBoard();
      }
    } catch (error) {
      await postToLoadBoard();
    }
  };

  if (
    !hasPhoneNumber &&
    loadboardsRequiringPhoneNumber.includes(integrationType)
  )
    return (
      <div className="flex flex-col gap-2">
        <InformationNeeded />
      </div>
    );

  async function postToLoadBoard() {
    await postToLB({
      variables: { input: { shipmentId, integrationType, postingUserId } },
      refetchQueries: [
        GetLoadBoardPostingsDocument,
        IsMovementPostedDocument,
        GetActivePostingsDocument,
      ],
    })
      .then(() => {
        sendToast(`Posted to ${lbString}`, {
          description: `Your shipment has been posted via ${lbString}.`,
          variant: 'success',
        });
      })
      .catch((e) => {
        sendToast(`Failed to post via ${lbString}`, {
          description: e.message,
          variant: 'error',
        });
      });
  }

  async function unpostFromLoadBoard() {
    await unpostFromLB({
      variables: { input: { shipmentId, integrationType } },
      refetchQueries: [
        GetLoadBoardPostingsDocument,
        IsMovementPostedDocument,
        GetActivePostingsDocument,
      ],
    })
      .then(() => {
        sendToast(`Unposted via ${lbString}`, {
          variant: 'success',
        });
      })
      .catch((e) => {
        sendToast(`Failed to unpost via ${lbString}`, {
          description: e.message,
          variant: 'error',
        });
      });
  }

  async function refreshPostingOnLoadBoard() {
    await refreshPosting({
      variables: { input: { shipmentId, integrationType } },
      refetchQueries: [
        GetLoadBoardPostingsDocument,
        IsMovementPostedDocument,
        GetActivePostingsDocument,
      ],
    })
      .then(() => {
        sendToast(`Refreshed on ${lbString}`, {
          description: `Your posting has been refreshed on ${lbString}.`,
          variant: 'success',
        });
      })
      .catch((e) => {
        sendToast(`Failed to refresh on ${lbString}`, {
          description: e.message,
          variant: 'error',
        });
      });
  }

  const handleToggle = (value: string) => {
    if (value === 'posted') {
      checkDistanceAndPost();
    } else {
      unpostFromLoadBoard();
    }
  };

  const isPosted = isPostedToLoadBoard(integrationType, posting);

  const postingUrl = posting?.postingUrl;
  const loading = postLoading || unpostLoading || refreshLoading;
  return (
    <div className="flex flex-row items-center gap-4">
      <Logo
        src={getIntegrationLogo(integrationType, logos)}
        tooltip={getIntegrationTooltip(integrationType)}
        width={48}
      />
      <Toggle
        value={isPosted ? 'posted' : 'unposted'}
        onValueChange={handleToggle}
      >
        {[
          { label: 'Unposted', value: 'unposted' },
          { label: 'Posted', value: 'posted' },
        ]}
      </Toggle>
      {isPosted && (
        <Button
          variant="secondary"
          size="xs"
          icon={<MdRefresh size={16} className="text-brand-500" />}
          tooltip={`Refresh posting on ${lbString}`}
          onPress={refreshPostingOnLoadBoard}
          isDisabled={loading}
        />
      )}
      {loading && <CircularProgress size="xs" />}
      {!isNil(postingUrl) && (
        <Button
          variant="secondary"
          size="xs"
          icon={<MdOutlineOpenInNew size={16} className="text-brand-500" />}
          tooltip={`View on ${lbString}`}
          onPress={() => window.open(postingUrl, '_blank')}
        />
      )}
      {!isPosted && (
        <PostingUserAutocomplete
          organizationId={defaultOrgId}
          onChange={setPostingUserId}
        />
      )}
    </div>
  );
}

type LogoProps = {
  src: string;
  tooltip: string;
  width?: number;
  imgClassName?: string;
};
export function Logo({ src, tooltip, imgClassName, width = 32 }: LogoProps) {
  return (
    <Tooltip content={tooltip} isInstant>
      <div className="flex-shrink-0 rounded-md border p-[2px]">
        <img className={imgClassName} width={width} src={src} alt={tooltip} />
      </div>
    </Tooltip>
  );
}
